/**
 * Composes the client-review feed rendered by the <Testimonials> and
 * <ServiceTestimonials> carousels.
 *
 * The CMS (Sanity) owns the Clutch reviews. This module layers two things on
 * top that are deliberately code-owned:
 *
 *  1. EVDEV's Upwork reviews — republished from a third-party profile, so the
 *     wording is tied to the original review and should only change when that
 *     review changes.
 *  2. A small set of pins/removals used to promote a stronger review over a
 *     weaker one without waiting on a CMS edit.
 *
 * Both are stopgaps in the sense that Sanity remains the editorial surface:
 * once a pinned review is added (or a superseded one deleted) in Studio, the
 * corresponding entry here should be removed.
 */

import type { ReviewData } from '@/sanity/types';
import { REVIEWERS } from '@/content/reviewers';

/** Badge copy for the per-review "verified on <platform>" chip. */
export const REVIEW_SOURCE_LABELS = {
  clutch: 'Verified on Clutch',
  upwork: 'Verified on Upwork',
} as const;

/**
 * Clutch reviews pinned to the front of the feed.
 *
 * Brights is EVDEV's Clutch *Featured Review* (5.0 across quality, schedule,
 * cost and willingness to refer) and describes a Shopify rebuild with
 * subscriptions, bundles and structured-data SEO — far stronger proof than the
 * chatbot review it replaces below. Quote is the Clutch feedback summary
 * verbatim, matching the entry already used on /clutch.
 */
export const PINNED_CLUTCH_REVIEWS: ReviewData[] = [
  {
    // Clutch: "E-Commerce Site Dev & Digital Mktg for Luxury Perfume House",
    // $50k–$199k, Jan 2024 – ongoing. Both sentences are the client's own
    // words, joined from the "measurable outcomes" and "most impressive"
    // answers. The doubled-sales figure is the client's own claim.
    review:
      'Within the first three months after launch, EVDEV’s work helped us double our sales. Our conversion rate improved as the store design, copywriting, and promotional landing pages came together. They pair genuine Shopify and ecommerce expertise with clear, steady communication, so we always felt in good hands.',
    ...REVIEWERS.imradSyed,
    photo: null,
    source: 'clutch',
  },
  {
    review:
      'EVDEV successfully launched the new store on schedule and provided great post-support. They managed the project professionally, established clear timelines, maintained regular communication, and provided consistent progress updates. Their technical expertise was commendable.',
    ...REVIEWERS.umen,
    photo: null,
    source: 'clutch',
  },
];

/**
 * CMS reviewers dropped from the carousel because a stronger review from the
 * same profile now covers the same ground.
 *
 *  - "Olexander Kukhar" (Owner, NeHorikh) — a chatbot build, off-topic for a
 *    Shopify agency and the thinnest quote in the set.
 *  - "Tatyana Buryanova" (CEO & Director, 180+) — "I enjoyed our collaboration,
 *    and I would definitely consider working with them again" says nothing
 *    about the work, and the company renders as the same "180+" string the
 *    stats grid uses, so the quote reads as a template bug.
 *
 * The Sanity rows are now gone (2026-09-10): filtering them here stopped them
 * rendering but not *publishing* — both were still serialized into the RSC
 * payload of `/`, and Buryanova into `/seo-services` and `/shopify-experts`
 * as well, where a scraper or an LLM crawler could still quote them. They were
 * removed from `homePage.sections[7].reviews`,
 * `seoServicesPage.sections[3].reviews` and
 * `shopifyExpertsPage.sections[4].reviews`.
 *
 * This list is kept as a tripwire rather than deleted with them: it costs
 * nothing and it stops either name rendering again if an editor re-adds it.
 * Matched on `clientName`, which is what the CMS keys reviewers by.
 */
const SUPERSEDED_CMS_REVIEWERS: readonly string[] = [
  'Olexander Kukhar',
  'Tatyana Buryanova',
];

/**
 * Verified EVDEV reviews from the public Upwork profile.
 *
 * Each quote is the client's own review with the subject rewritten from the
 * founder's name (Misha / Mykhailo) to EVDEV, and trimmed for length. No claim,
 * metric, company, or role has been added that is not in the original review.
 *
 * `clientTitle` currently holds an honest descriptor derived from the Upwork job
 * itself — only "Owner, Soft Interrogations" names a real company (the brand was
 * in the job title). Replace these with real titles/companies as clients confirm
 * them.
 *
 * `photo: null` → the carousel draws the initials avatar.
 */
export const UPWORK_REVIEWS: ReviewData[] = [
  {
    // Upwork: "Shopify Developer to Build Multi-Step Product Customizer", $6,000 fixed
    review:
      'An amazing experience working with EVDEV, and I’d highly recommend them to anyone looking for a serious, reliable Shopify expert. Communication was clear and proactive throughout, timelines were respected, and progress updates were frequent and transparent. EVDEV has an excellent grasp of Shopify development from both a business and technical perspective.',
    ...REVIEWERS.maxim,
    photo: null,
    source: 'upwork',
  },
  {
    // Upwork: "Shopify Development Project for Online Store", 71h hourly
    review:
      'EVDEV has been a tremendous help on our Shopify custom-theme development projects. Deep knowledge of Shopify development (Liquid, JS, CSS) and able to handle complex solutions for our website. I highly recommend EVDEV for any Shopify technical or development work.',
    ...REVIEWERS.jason,
    photo: null,
    source: 'upwork',
  },
  {
    // Upwork: "Ongoing website edits/development", 20-month retainer
    review:
      'EVDEV is incredible to work with. I’ve been on Upwork a long time, as both a freelancer and a client, and EVDEV is truly a rare find on this platform. Work happens quickly, instructions are followed to the letter, and the communication and helpful input are always invaluable.',
    ...REVIEWERS.aarielle,
    photo: null,
    source: 'upwork',
  },
  {
    // Upwork: "Web Developer For E-Commerce Site | Agency Work", $3,000 fixed
    review:
      'EVDEV was awesome to work with. Super competent development and great communication throughout the project. Highly recommend.',
    ...REVIEWERS.mit,
    photo: null,
    source: 'upwork',
  },
  {
    // Upwork: "the utter shopify store for Soft Interrogations"
    review:
      'EVDEV was incredible to work with and made sure my store had some really awesome finishing touches. Thank you Misha and the EVDEV team for the attention to detail.',
    ...REVIEWERS.yrsa,
    photo: null,
    source: 'upwork',
  },
  {
    // Upwork: "Convert Tailwind HTML / CSS site to Next.JS / Storefront API"
    review:
      'EVDEV was great. Did an amazing job and I was really happy with the outcome. Already planning on using EVDEV for another project.',
    ...REVIEWERS.adam,
    photo: null,
    source: 'upwork',
  },
];

/**
 * Build the carousel feed: drop superseded CMS reviews, pin the featured Clutch
 * ones to the front, then interleave the Upwork reviews so the carousel
 * alternates between platforms instead of showing one block of each. Handles
 * uneven lengths — whichever list runs out first, the rest of the other is
 * appended.
 *
 * A pinned review is skipped if the CMS already contains a review from the same
 * person, so adding it in Studio later does not produce a duplicate.
 */
export function buildTestimonialFeed(cmsReviews: ReviewData[]): ReviewData[] {
  const kept = cmsReviews.filter(
    (r) => !SUPERSEDED_CMS_REVIEWERS.includes(r.clientName),
  );
  const cmsNames = new Set(kept.map((r) => r.clientName));
  const pinned = PINNED_CLUTCH_REVIEWS.filter(
    (r) => !cmsNames.has(r.clientName),
  );

  const clutchReviews = [...pinned, ...kept];
  const merged: ReviewData[] = [];
  const longest = Math.max(clutchReviews.length, UPWORK_REVIEWS.length);

  for (let i = 0; i < longest; i += 1) {
    const clutch = clutchReviews[i];
    const upwork = UPWORK_REVIEWS[i];
    if (clutch) merged.push(clutch);
    if (upwork) merged.push(upwork);
  }

  return merged;
}
